import CobraSpinner from '../shared/CobraSpinner';
import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Text,
  useToast,
  Skeleton,
  
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Avatar,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Tooltip,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  BellIcon, 
  SettingsIcon, 
  CheckIcon, 
  DeleteIcon,
  TimeIcon,
  ViewIcon,
  ChevronDownIcon
} from '@chakra-ui/icons';
import { 
  FiBell, 
  FiMail, 
  FiMessageSquare, 
  FiCalendar,
  FiRefreshCw,
  FiFilter,
  FiCheck,
  FiX,
  FiEye,
  FiTrash2,
  FiSettings,
  FiVolume2,
  FiVolumeX,
  FiStar,
  FiClock,
  FiUser,
  FiBookOpen
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const NotificationCard = ({ notification, onMarkAsRead, onDelete, primaryColor, secondaryColor, textColor }) => {
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'course': return FiBookOpen;
      case 'event': return FiCalendar;
      case 'message': return FiMessageSquare;
      case 'system': return FiBell;
      case 'grade': return FiStar;
      default: return FiBell;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'course': return 'blue';
      case 'event': return 'green';
      case 'message': return 'purple';
      case 'system': return 'orange';
      case 'grade': return 'yellow';
      default: return 'gray';
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      course: 'دورة',
      event: 'حدث',
      message: 'رسالة',
      system: 'نظام',
      grade: 'درجة'
    };
    return types[type] || type;
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        bg={notification.isRead ? 'gray.50' : 'white'} 
        borderRadius="xl" 
        boxShadow="md" 
        border="1px solid" 
        borderColor={notification.isRead ? 'gray.200' : primaryColor}
        borderLeftWidth={notification.isRead ? '1px' : '4px'}
        borderLeftColor={notification.isRead ? 'gray.200' : primaryColor}
        _hover={{ boxShadow: 'lg' }}
      >
        <CardBody p={6}>
          <Flex justify="space-between" align="start" gap={4}>
            <HStack spacing={4} flex="1">
              <Box 
                bg={`${getNotificationColor(notification.type)}.100`} 
                p={3} 
                borderRadius="lg"
              >
                <Icon 
                  as={getNotificationIcon(notification.type)} 
                  color={`${getNotificationColor(notification.type)}.500`} 
                  boxSize={5} 
                />
              </Box>
              
              <VStack align="start" spacing={2} flex="1">
                <HStack spacing={2}>
                  <Badge 
                    colorScheme={getNotificationColor(notification.type)} 
                    variant="subtle"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontFamily="Tajawal, sans-serif"
                  >
                    {getTypeLabel(notification.type)}
                  </Badge>
                  
                  {!notification.isRead && (
                    <Badge 
                      colorScheme="red" 
                      variant="solid"
                      borderRadius="full"
                      px={2}
                      py={1}
                      fontSize="xs"
                      fontFamily="Tajawal, sans-serif"
                    >
                      جديد
                    </Badge>
                  )}
                </HStack>
                
                <Heading 
                  size="sm" 
                  color={textColor} 
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="600"
                  noOfLines={2}
                >
                  {notification.title}
                </Heading>
                
                <Text 
                  color="gray.600" 
                  fontSize="sm" 
                  fontFamily="Tajawal, sans-serif"
                  noOfLines={3}
                >
                  {notification.message}
                </Text>
                
                <HStack spacing={4} fontSize="xs" color="gray.500">
                  <HStack spacing={1}>
                    <Icon as={FiClock} />
                    <Text fontFamily="Tajawal, sans-serif">
                      {formatDistanceToNow(new Date(notification.createdAt), { 
                        addSuffix: true, 
                        locale: ar 
                      })}
                    </Text>
                  </HStack>
                  
                  {notification.sender && (
                    <HStack spacing={1}>
                      <Icon as={FiUser} />
                      <Text fontFamily="Tajawal, sans-serif">
                        {notification.sender}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            </HStack>
            
            <VStack spacing={2}>
              <Menu>
                <MenuButton 
                  as={IconButton} 
                  icon={<ChevronDownIcon />} 
                  variant="ghost" 
                  size="sm"
                />
                <MenuList>
                  {!notification.isRead && (
                    <MenuItem 
                      icon={<FiCheck />} 
                      onClick={() => onMarkAsRead(notification._id)}
                      fontFamily="Tajawal, sans-serif"
                    >
                      تحديد كمقروء
                    </MenuItem>
                  )}
                  <MenuItem 
                    icon={<FiEye />} 
                    fontFamily="Tajawal, sans-serif"
                  >
                    عرض التفاصيل
                  </MenuItem>
                  <MenuItem 
                    icon={<FiTrash2 />} 
                    color="red.500"
                    onClick={() => onDelete(notification.id)}
                    fontFamily="Tajawal, sans-serif"
                  >
                    حذف
                  </MenuItem>
                </MenuList>
              </Menu>
            </VStack>
          </Flex>
        </CardBody>
      </Card>
    </motion.div>
  );
};

const NotificationsContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  
  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    courseUpdates: true,
    eventReminders: true,
    gradeNotifications: true,
    systemMessages: true
  });

  const notificationTypes = [
    { value: 'all', label: 'جميع الأنواع' },
    { value: 'course', label: 'الدورات' },
    { value: 'event', label: 'الأحداث' },
    { value: 'message', label: 'الرسائل' },
    { value: 'system', label: 'النظام' },
    { value: 'grade', label: 'الدرجات' }
  ];

  const statusFilters = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'unread', label: 'غير مقروءة' },
    { value: 'read', label: 'مقروءة' }
  ];

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    filterNotifications();
  }, [searchQuery, typeFilter, statusFilter, notifications]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
      // Mock data for demonstration
      const mockNotifications = [
        {
          id: 1,
          title: 'محاضرة جديدة متاحة',
          message: 'تم إضافة محاضرة جديدة في دورة الرياضيات المتقدمة. يمكنك الآن مشاهدتها والاستفادة من المحتوى الجديد.',
          type: 'course',
          isRead: false,
          createdAt: new Date().toISOString(),
          sender: 'د. أحمد محمد'
        },
        {
          id: 2,
          title: 'تذكير بامتحان غداً',
          message: 'لا تنس أن لديك امتحان الفيزياء النووية غداً في الساعة 10:00 صباحاً. تأكد من مراجعة المواد.',
          type: 'event',
          isRead: false,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          sender: 'النظام'
        },
        {
          id: 3,
          title: 'تم تقييم واجبك',
          message: 'تم تقييم واجب الكيمياء العضوية الخاص بك. حصلت على درجة 85/100. يمكنك مراجعة التفاصيل.',
          type: 'grade',
          isRead: true,
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          sender: 'د. فاطمة علي'
        },
        {
          id: 4,
          title: 'رسالة جديدة من المدرس',
          message: 'لديك رسالة جديدة من د. محمد حسن بخصوص مشروع نهاية الفصل. يرجى قراءتها في أقرب وقت.',
          type: 'message',
          isRead: false,
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          sender: 'د. محمد حسن'
        },
        {
          id: 5,
          title: 'تحديث النظام',
          message: 'تم تحديث النظام إلى الإصدار الجديد. تتضمن التحديثات تحسينات في الأداء وميزات جديدة.',
          type: 'system',
          isRead: true,
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          sender: 'فريق التطوير'
        }
      ];
      setNotifications(mockNotifications);
    } finally {
      setLoading(false);
    }
  };

  const filterNotifications = () => {
    let filtered = notifications;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(notification =>
        notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (notification.sender && notification.sender.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(notification => notification.type === typeFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(notification => {
        if (statusFilter === 'read') return notification.isRead;
        if (statusFilter === 'unread') return !notification.isRead;
        return true;
      });
    }

    setFilteredNotifications(filtered);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axiosInstance.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      );
      toast({
        title: 'تم التحديث',
        description: 'تم تحديد الإشعار كمقروء',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('خطأ في تحديث الإشعار:', error);
      // Update locally for demo
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axiosInstance.patch('/notifications/mark-all-read');
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      toast({
        title: 'تم التحديث',
        description: 'تم تحديد جميع الإشعارات كمقروءة',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('خطأ في تحديث الإشعارات:', error);
      // Update locally for demo
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await axiosInstance.delete(`/notifications/${notificationId}`);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
      toast({
        title: 'تم الحذف',
        description: 'تم حذف الإشعار بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('خطأ في حذف الإشعار:', error);
      // Delete locally for demo
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    }
  };

  const handleClearAll = async () => {
    try {
      await axiosInstance.delete('/notifications/clear-all');
      setNotifications([]);
      toast({
        title: 'تم المسح',
        description: 'تم مسح جميع الإشعارات',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('خطأ في مسح الإشعارات:', error);
      // Clear locally for demo
      setNotifications([]);
    }
  };

  const getNotificationStats = () => {
    const totalNotifications = notifications.length;
    const unreadNotifications = notifications.filter(n => !n.isRead).length;
    const todayNotifications = notifications.filter(n => {
      const notificationDate = new Date(n.createdAt);
      const today = new Date();
      return notificationDate.toDateString() === today.toDateString();
    }).length;
    const importantNotifications = notifications.filter(n => 
      n.type === 'grade' || n.type === 'event'
    ).length;

    return { totalNotifications, unreadNotifications, todayNotifications, importantNotifications };
  };

  const stats = getNotificationStats();

  if (loading) {
    return (
      <Flex justify="center" align="center" height="100vh" dir="rtl">
        <VStack spacing={4}>
          <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل الإشعارات...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          mr={{ base: '0px', md: isOpen ? '250px' : '100px' }}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading 
                      as="h1" 
                      size="xl" 
                      color={secondaryColor} 
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      الإشعارات
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      تابع آخر التحديثات والرسائل المهمة
                    </Text>
                  </VStack>
                  
                  <HStack spacing={3} flexDirection={{ base: 'column', sm: 'row' }} w={{ base: 'full', md: 'auto' }}>
                    <Button
                      bg="blue.500"
                      color="white"
                      _hover={{ bg: 'blue.600' }}
                      borderRadius="lg"
                      fontWeight="600"
                      leftIcon={<FiCheck />}
                      onClick={handleMarkAllAsRead}
                      fontFamily="Tajawal, sans-serif"
                      isDisabled={stats.unreadNotifications === 0}
                    >
                      تحديد الكل كمقروء
                    </Button>
                    
                    <Button
                      variant="outline"
                      borderColor="red.300"
                      color="red.500"
                      _hover={{ bg: 'red.50' }}
                      borderRadius="lg"
                      fontWeight="600"
                      leftIcon={<FiTrash2 />}
                      onClick={handleClearAll}
                      fontFamily="Tajawal, sans-serif"
                    >
                      مسح الكل
                    </Button>
                    
                    <IconButton
                      icon={<FiSettings />}
                      aria-label="إعدادات الإشعارات"
                      bg="white"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                      borderRadius="lg"
                      onClick={onSettingsOpen}
                    />
                    
                    <IconButton
                      icon={<FiRefreshCw />}
                      aria-label="تحديث"
                      bg="white"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                      borderRadius="lg"
                      onClick={fetchNotifications}
                      isLoading={loading}
                    />
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إجمالي الإشعارات
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.totalNotifications}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            جميع الإشعارات
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg={`${primaryColor}20`} 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiBell} color={primaryColor} boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            غير مقروءة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.unreadNotifications}
                          </StatNumber>
                          <StatHelpText color="red.500" fontSize="sm">
                            تحتاج انتباه
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="red.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiMail} color="red.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إشعارات اليوم
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.todayNotifications}
                          </StatNumber>
                          <StatHelpText color="green.500" fontSize="sm">
                            <StatArrow type="increase" />
                            جديدة اليوم
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="green.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiClock} color="green.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            مهمة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.importantNotifications}
                          </StatNumber>
                          <StatHelpText color="orange.500" fontSize="sm">
                            درجات وأحداث
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="orange.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiStar} color="orange.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

         

          {/* Notifications List */}
          <motion.div variants={fadeIn}>
            {filteredNotifications.length > 0 ? (
              <VStack spacing={4} align="stretch">
                {filteredNotifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDeleteNotification}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                  />
                ))}
              </VStack>
            ) : (
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack spacing={6} p={12} textAlign="center">
                    <Icon as={FiBell} boxSize="3rem" color="gray.600" />
                    <VStack spacing={2}>
                      <Heading as="h3" size="md" color="gray.600" fontFamily="Tajawal, sans-serif">
                        لا توجد إشعارات
                      </Heading>
                      <Text color="gray.500" fontFamily="Tajawal, sans-serif">
                        {searchQuery || typeFilter !== 'all' || statusFilter !== 'all' 
                          ? 'لم يتم العثور على إشعارات تطابق البحث'
                          : 'ستظهر الإشعارات الجديدة هنا'
                        }
                      </Text>
                    </VStack>
                    {(searchQuery || typeFilter !== 'all' || statusFilter !== 'all') && (
                      <Button 
                        bg={primaryColor}
                        color={secondaryColor}
                        _hover={{ bg: '#d4b016' }}
                        onClick={() => {
                          setSearchQuery('');
                          setTypeFilter('all');
                          setStatusFilter('all');
                        }}
                        fontFamily="Tajawal, sans-serif"
                      >
                        مسح الفلاتر
                      </Button>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </motion.div>

          {/* Settings Modal */}
          <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="lg">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" fontFamily="Tajawal, sans-serif">
              <ModalHeader bg={secondaryColor} color="white" borderTopRadius="xl">
                إعدادات الإشعارات
              </ModalHeader>
              <ModalCloseButton color="white" />
              <ModalBody p={6}>
                <VStack spacing={6} align="stretch">
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="email-notifications" mb="0" fontFamily="Tajawal, sans-serif">
                      إشعارات البريد الإلكتروني
                    </FormLabel>
                    <Switch 
                      id="email-notifications" 
                      colorScheme="yellow"
                      isChecked={settings.emailNotifications}
                      onChange={(e) => setSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="push-notifications" mb="0" fontFamily="Tajawal, sans-serif">
                      الإشعارات الفورية
                    </FormLabel>
                    <Switch 
                      id="push-notifications" 
                      colorScheme="yellow"
                      isChecked={settings.pushNotifications}
                      onChange={(e) => setSettings(prev => ({ ...prev, pushNotifications: e.target.checked }))}
                    />
                  </FormControl>

                  <Divider />

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="course-updates" mb="0" fontFamily="Tajawal, sans-serif">
                      تحديثات الدورات
                    </FormLabel>
                    <Switch 
                      id="course-updates" 
                      colorScheme="yellow"
                      isChecked={settings.courseUpdates}
                      onChange={(e) => setSettings(prev => ({ ...prev, courseUpdates: e.target.checked }))}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="event-reminders" mb="0" fontFamily="Tajawal, sans-serif">
                      تذكيرات الأحداث
                    </FormLabel>
                    <Switch 
                      id="event-reminders" 
                      colorScheme="yellow"
                      isChecked={settings.eventReminders}
                      onChange={(e) => setSettings(prev => ({ ...prev, eventReminders: e.target.checked }))}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="grade-notifications" mb="0" fontFamily="Tajawal, sans-serif">
                      إشعارات الدرجات
                    </FormLabel>
                    <Switch 
                      id="grade-notifications" 
                      colorScheme="yellow"
                      isChecked={settings.gradeNotifications}
                      onChange={(e) => setSettings(prev => ({ ...prev, gradeNotifications: e.target.checked }))}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="system-messages" mb="0" fontFamily="Tajawal, sans-serif">
                      رسائل النظام
                    </FormLabel>
                    <Switch 
                      id="system-messages" 
                      colorScheme="yellow"
                      isChecked={settings.systemMessages}
                      onChange={(e) => setSettings(prev => ({ ...prev, systemMessages: e.target.checked }))}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter bg="gray.50" borderBottomRadius="xl">
                <HStack spacing={3}>
                  <Button 
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#d4b016' }}
                    onClick={() => {
                      toast({
                        title: 'تم الحفظ',
                        description: 'تم حفظ إعدادات الإشعارات بنجاح',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                      });
                      onSettingsClose();
                    }}
                    fontFamily="Tajawal, sans-serif"
                  >
                    حفظ الإعدادات
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={onSettingsClose}
                    fontFamily="Tajawal, sans-serif"
                  >
                    إلغاء
                  </Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Flex>
    </Box>
  );
};

const Notifications = () => {
  return (
    <SidebarProvider>
      <NotificationsContent />
    </SidebarProvider>
  );
};

export default Notifications;


